<template>
  <div class="pr-1 sm:overflow-auto md:overflow-hidden lg:overflow-hidden">
    <TableWithCondensedContent
      class="mt-4"
      has-cell-link
      cell-link-text="Go to proposal"
      :table-headers="['Client', 'Project / Budget', 'Proposal Heading', 'Updated / Starts', 'Status']"
      sort-key="Updated / Starts"
      sort-order="desc"
      :table-rows="userProposalsRecent.filter((proposal) => (proposal.visible && proposal.project.status !== 'archived')).map((proposal) => {
        return [
          proposal.client.name,
          [
            proposal.project.name ?? '-',
            proposal.data.destination.name + ' &bull; ' + (proposal.project.total_travelers ?? '0') + ' pax' + ' &times; ' + (formatCurrency(proposal.project.budget_per_traveller) ?? '-'),
          ],
          [
            proposal.title ?? '-',
            proposal.data.destination.heading ?? '-',
          ],
          [
            proposal.updated_at ? getDateTimeInCurrentTimezone(proposal.updated_at, 'DD.MM.YYYY') : '-',
            '&nbsp;&nbsp;&nbsp;' + (proposal.start_date ? getDateTimeInDestinationTimezone(proposal.start_date, proposal.data.destination.timezone, 'DD.MM.YYYY') : '-'),
          ],
          {
            type: 'status-badge',
            value: proposal.status,
          },
          route('proposals.show', {id: proposal.id}),
        ];
      })">
      <template #header>
        <Tooltip
          align="left"
          content-class="text-xs italic text-gray-400 hover:text-gray-400 -mb-4 w-max">
          <template #trigger>
            <h2 class="relative w-fit cursor-help text-lg font-medium text-gray-800">
              Your active proposals
            </h2>
          </template>
          <template #content>
            Showing up to 10 of your most recently updated proposals within the last 60 days, excluding hidden or those from archived projects.
          </template>
        </Tooltip>
      </template>
    </TableWithCondensedContent>

    <TableWithCondensedContent
      class="my-8"
      has-cell-link
      has-search
      has-paginator
      :items-per-page="10"
      :button="{ text: 'Create new proposal', link: route('proposals.create') }"
      search-placeholder="Search proposals"
      cell-link-text="Go to proposal"
      :table-headers="['Client / PM', 'Project / Budget', 'Proposal Heading', 'Created / Starts', 'Status']"
      sort-key="Created / Starts"
      sort-order="desc"
      :table-rows="teamProposals.filter((proposal) => (proposal.visible)).map((proposal) => {
        return [
          [
            proposal.client.name,
            proposal.project.manager?.name ?? '-',
          ],
          [
            proposal.project.name,
            proposal.data.destination.name + ' &bull; ' + (proposal.project.total_travelers ?? '0') + ' pax' + ' &times; ' + (formatCurrency(proposal.project.budget_per_traveller) ?? '-'),
          ],
          [
            proposal.title ?? '-',
            proposal.data.destination.heading ?? '-',
          ],
          [
            proposal.created_at ? getDateTimeInCurrentTimezone(proposal.created_at, 'DD.MM.YYYY') : '-',
            '&nbsp;&nbsp;&nbsp;' + (proposal.start_date ? getDateTimeInDestinationTimezone(proposal.start_date, proposal.data.destination.timezone, 'DD.MM.YYYY') : '-'),
          ],
          {
            type: 'status-badge',
            value: proposal.status,
          },
          route('proposals.show', {id: proposal.id}),
        ];
      })">
      <template #header>
        <Tooltip
          class="mb-2 w-fit"
          align="left"
          content-class="text-xs italic text-gray-400 hover:text-gray-400 -mb-4 w-max">
          <template #trigger>
            <h2 class="relative w-fit cursor-help text-lg font-medium text-gray-800">
              Your team's proposals
            </h2>
          </template>
          <template #content>
            Every proposal (including yours) found in your team, <strong>{{ team.name }}</strong>, excluding hidden proposals.
          </template>
        </Tooltip>
      </template>
    </TableWithCondensedContent>
  </div>
  <div class="relative mt-5 overflow-hidden rounded-lg bg-white shadow" />
</template>

<script setup>
/* external */
import {
  useAttrs,
} from 'vue';

/* local */
import TableWithCondensedContent from '@/Components/Lists/TableWithCondensedContent.vue';
import {
  formatCurrency, getDateTimeInCurrentTimezone, getDateTimeInDestinationTimezone,
} from '@/utils';
import Tooltip from '@/Components/Tooltip.vue';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */
defineProps({
  userProposalsRecent: {
    type: Array,
    required: true,
    default: () => [],
  },
  teamProposals: {
    type: Array,
    required: true,
    default: () => [],
  },
});

const attrs = useAttrs();
const { team } = attrs;

</script>
